import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../client';
import { queryKeys } from '../query-keys';
import type {
  Product,
  ProductSearchParams,
  ProductSearchResult,
} from '../types';

/**
 * Build query string from product search parameters.
 */
function buildSearchQueryString(params: ProductSearchParams): string {
  const searchParams = new URLSearchParams();

  if (params.q) searchParams.set('q', params.q);
  if (params.barcode) searchParams.set('barcode', params.barcode);
  if (params.country) searchParams.set('country', params.country);
  if (params.includeExternal !== undefined) {
    searchParams.set('includeExternal', String(params.includeExternal));
  }
  if (params.limit) searchParams.set('limit', String(params.limit));

  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : '';
}

/**
 * Search products for adding to inventory.
 * Searches both local database and Open Food Facts.
 *
 * @param householdId - Household UUID to get default country
 * @param params - Search parameters
 */
async function searchProducts(
  householdId: string,
  params: ProductSearchParams
): Promise<ProductSearchResult> {
  const queryString = buildSearchQueryString(params);
  return apiFetch<ProductSearchResult>(
    `/households/${householdId}/items/search-products${queryString}`
  );
}

/**
 * Materialize an external product by barcode.
 * Fetches from Open Food Facts and saves to local database.
 * Use this when user selects an external product from search results.
 *
 * @param barcode - Product barcode to materialize
 * @returns The saved product with a local database ID
 */
export async function materializeExternalProduct(
  barcode: string
): Promise<Product> {
  return apiFetch<Product>(`/products/barcode/${barcode}`);
}

/**
 * Hook to search products for adding to inventory.
 * Searches both local database and Open Food Facts API.
 *
 * @param householdId - Household UUID (uses household's country as default)
 * @param params - Search parameters (q for text search, barcode for exact match)
 * @param options - Query options including enabled flag
 *
 * @example
 * ```tsx
 * // Text search
 * const { data } = useSearchProducts(householdId, { q: 'nutella' });
 *
 * // Barcode lookup
 * const { data } = useSearchProducts(householdId, { barcode: '3017620425035' });
 *
 * // Conditional fetching (e.g., after barcode scan)
 * const { data } = useSearchProducts(householdId, { barcode: scannedCode }, { enabled: !!scannedCode });
 * ```
 */
export function useSearchProducts(
  householdId: string,
  params: ProductSearchParams,
  options?: { enabled?: boolean }
) {
  // Query is enabled when:
  // - householdId is provided
  // - AND (q has at least 2 chars OR barcode is provided)
  // - AND options.enabled is not explicitly false
  const hasValidQuery =
    !!(params.q && params.q.length >= 2) || !!params.barcode;

  return useQuery({
    queryKey: queryKeys.items.searchProducts(householdId, params),
    queryFn: () => searchProducts(householdId, params),
    enabled: options?.enabled !== false && !!householdId && hasValidQuery,
    staleTime: 30 * 1000, // 30 seconds - search results are fairly transient
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
}
